import { TabContext, TabList, TabPanel } from "@mui/lab";
import { Box, Tab } from "@mui/material";
import { useState, useMemo, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import CategoryDto from "../../../models/CategoryDto";
import SubCategoryDto from "../../../models/SubCategoryDto";
import { useRestaurantData } from "../../../provider/RestaurantDataContext";
import ContextPath from "../contextPath/ContextPath";
import SubCategoryPage from "../subCategoryPage/SubCategoryPage";
import { SUB_CATEGORY_URL } from "../../../utils/Constants";

enum SubCategoryChange {
    URL,
    TAB
}

export default function CategoryPage() {
    const { restaurantData, restaurantData: { currency } } = useRestaurantData();
    const params = useParams();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const blockRedirect = useRef<SubCategoryChange | undefined>(undefined);

    const [subCategoryId, setSubCategoryId] = useState(params.subCategory || '-1');

    const category = useMemo(() => {
        const category = restaurantData.categories.filter((category: CategoryDto) => category.id === Number(params.mainCategory))[0];
        if (subCategoryId === '-1') {
            const subCategories = category.subCategories;
            const subCategoryId = subCategories.length > 0 ? subCategories[0].id.toString() : '0';
            setSubCategoryId(subCategoryId);
        }
        return category;
    }, [params.mainCategory, restaurantData.categories, subCategoryId]);

    useEffect(() => {
        if (params.subCategory && params.subCategory !== subCategoryId && blockRedirect.current === undefined) {
            blockRedirect.current = SubCategoryChange.URL;
            setSubCategoryId(params.subCategory);
        } else if (blockRedirect.current === SubCategoryChange.TAB || blockRedirect.current === SubCategoryChange.URL) {
            blockRedirect.current = undefined;
        }
    }, [params.subCategory, subCategoryId]);

    useEffect(() => {
        let newPath = "";
        if (!pathname.includes(SUB_CATEGORY_URL)) {
            newPath = SUB_CATEGORY_URL + '/';
        } else {
            newPath = '../'
        }
        navigate(newPath + subCategoryId, { relative: 'path' });
    }, [subCategoryId, navigate, pathname]);

    const handleChange = (_event: React.SyntheticEvent, newValue: any) => {
        blockRedirect.current = SubCategoryChange.TAB;
        setSubCategoryId(newValue);
    };

    return (
        <Box sx={{ width: "100%" }}>
            <ContextPath />
            <TabContext value={subCategoryId}>
                <Box
                    sx={{
                        overflow: 'hidden',
                        borderBottom: 1,
                        bgcolor: "white",
                        borderRadius: "60rem",
                        mt: "0.625rem",
                        mb: '-0.625rem',
                    }}
                >
                    <TabList
                        onChange={handleChange}
                        aria-label="subCategory"
                        variant="scrollable"
                        scrollButtons="auto"
                    >
                        {category.subCategories.map(
                            (subCategory: SubCategoryDto, index: number) => {
                                return (
                                    <Tab
                                        label={subCategory.value}
                                        value={subCategory.id.toString()}
                                        key={index}
                                        sx={{
                                            ml: index === 0 ? "0.375rem" : '',
                                            mr: index === category.subCategories.length - 1 ? "0.375" : '',
                                        }}
                                        wrapped
                                    />
                                );
                            }
                        )}
                    </TabList>
                </Box>
                {category.subCategories.map(
                    (subCategory: SubCategoryDto, index: number) => (
                        <TabPanel value={subCategory.id.toString()} key={index} sx={{ pl: '0', pr: '0' }}>
                            <SubCategoryPage subCategory={subCategory} currency={currency} />
                        </TabPanel>
                    )
                )}
            </TabContext>
        </Box>
    );
}